import Alpine from 'alpinejs';

import { isMotionFull, subscribeMotion } from '../motion';

type MagneticBtnData = {
  curX: number;
  curY: number;
  tgX: number;
  tgY: number;
  active: boolean;
  frameId: number | null;
  onMove: ((event: MouseEvent) => void) | null;
  unsubscribe: (() => void) | null;
  init(this: MagneticBtnData & { $el: HTMLElement }): void;
  start(this: MagneticBtnData & { $el: HTMLElement }): void;
  stop(this: MagneticBtnData & { $el: HTMLElement }): void;
  destroy(this: MagneticBtnData & { $el: HTMLElement }): void;
};

const MAGNETIC_MAX = 10;
const MAGNETIC_LERP = 18;
const MAGNETIC_STRENGTH = 0.25;
const MAGNETIC_ZONE = 25;

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const isInMagneticZone = (
  element: HTMLElement,
  x: number,
  y: number,
): boolean => {
  const rect = element.getBoundingClientRect();
  return (
    x >= rect.left - MAGNETIC_ZONE &&
    x <= rect.right + MAGNETIC_ZONE &&
    y >= rect.top - MAGNETIC_ZONE &&
    y <= rect.bottom + MAGNETIC_ZONE
  );
};

const isMagneticAllowed = (): boolean =>
  isMotionFull() && !window.matchMedia('(pointer: coarse)').matches;

export const registerMagneticBtn = (): void => {
  Alpine.data(
    'magneticBtn',
    (): MagneticBtnData => ({
      curX: 0,
      curY: 0,
      tgX: 0,
      tgY: 0,
      active: false,
      frameId: null,
      onMove: null,
      unsubscribe: null,

      init() {
        const icons = [...this.$el.querySelectorAll('svg')];
        if (icons.length === 0) {
          return;
        }

        const sharedParent =
          icons.length > 1 &&
          icons.every((icon) => icon.parentElement === icons[0].parentElement)
            ? icons[0].parentElement
            : null;

        const wrapper =
          sharedParent &&
          [...sharedParent.children].every(
            (child) => child instanceof SVGElement,
          )
            ? sharedParent
            : null;

        const transformTargets: (HTMLElement | SVGSVGElement)[] = wrapper
          ? [wrapper]
          : icons;

        const getTargetBounds = (): DOMRect => {
          if (wrapper) {
            return wrapper.getBoundingClientRect();
          }

          const visibleIcons = icons.filter(
            (icon) => icon.getClientRects().length > 0,
          );
          const measureIcons = visibleIcons.length > 0 ? visibleIcons : icons;

          let left = Infinity;
          let top = Infinity;
          let right = -Infinity;
          let bottom = -Infinity;

          for (const icon of measureIcons) {
            const rect = icon.getBoundingClientRect();
            left = Math.min(left, rect.left);
            top = Math.min(top, rect.top);
            right = Math.max(right, rect.right);
            bottom = Math.max(bottom, rect.bottom);
          }

          return new DOMRect(left, top, right - left, bottom - top);
        };

        const onMove = (event: MouseEvent): void => {
          const inZone = isInMagneticZone(
            this.$el,
            event.clientX,
            event.clientY,
          );

          if (!inZone) {
            if (this.active) {
              this.active = false;
              this.tgX = 0;
              this.tgY = 0;
            }
            return;
          }

          this.active = true;

          const iconRect = getTargetBounds();
          const iconCenterX = iconRect.left + iconRect.width / 2 - this.curX;
          const iconCenterY = iconRect.top + iconRect.height / 2 - this.curY;
          const dx = event.clientX - iconCenterX;
          const dy = event.clientY - iconCenterY;

          this.tgX = clamp(dx * MAGNETIC_STRENGTH, -MAGNETIC_MAX, MAGNETIC_MAX);
          this.tgY = clamp(dy * MAGNETIC_STRENGTH, -MAGNETIC_MAX, MAGNETIC_MAX);
        };

        const animate = (): void => {
          this.curX += (this.tgX - this.curX) / MAGNETIC_LERP;
          this.curY += (this.tgY - this.curY) / MAGNETIC_LERP;
          const transform = `translate(${this.curX}px, ${this.curY}px)`;
          for (const el of transformTargets) {
            el.style.transform = transform;
          }
          this.frameId = requestAnimationFrame(animate);
        };

        this.start = () => {
          if (this.frameId !== null) {
            return;
          }
          this.onMove = onMove;
          window.addEventListener('mousemove', onMove);
          this.frameId = requestAnimationFrame(animate);
        };

        this.stop = () => {
          if (this.frameId !== null) {
            cancelAnimationFrame(this.frameId);
            this.frameId = null;
          }
          if (this.onMove) {
            window.removeEventListener('mousemove', this.onMove);
            this.onMove = null;
          }
          this.active = false;
          this.tgX = 0;
          this.tgY = 0;
          this.curX = 0;
          this.curY = 0;
          for (const el of transformTargets) {
            el.style.transform = '';
          }
        };

        const sync = (): void => {
          if (isMagneticAllowed()) {
            this.start();
          } else {
            this.stop();
          }
        };

        sync();
        this.unsubscribe = subscribeMotion(sync);
      },

      start() {
        // Replaced in init once targets are known
      },

      stop() {
        // Replaced in init once targets are known
      },

      destroy() {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.stop();
      },
    }),
  );
};

export const initMagneticBtns = (): void => {
  document.addEventListener('alpine:initialized', () => {
    for (const btn of document.querySelectorAll<HTMLElement>('.btn')) {
      if (!btn.hasAttribute('x-data')) {
        btn.setAttribute('x-data', 'magneticBtn');
        Alpine.initTree(btn);
      }
    }
  });
};
